// frontend/src/context/FarmContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const FarmContext = createContext(null);

export const FarmProvider = ({ children }) => {
  const { user } = useAuth();

  const [farms, setFarms] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState(
    () => sessionStorage.getItem('selectedFarmId') || ''
  );
  const [farmSummary, setFarmSummary] = useState(null);
  const [selectedPlotId, setSelectedPlotId] = useState(null);

  // Cache for detailed plot conditions: { [plotId]: plotConditionsData }
  const [plotConditionsCache, setPlotConditionsCache] = useState({});

  // Devices state
  const [devices, setDevices] = useState([]);

  // Loading states
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [error, setError] = useState(null);

  // 1. Fetch available farms on initial load or user change
  const fetchFarms = useCallback(async () => {
    if (!user) {
      setFarms([]);
      setSelectedFarmId(null);
      setError(null);
      sessionStorage.removeItem('selectedFarmId');
      return;
    }

    setLoadingFarms(true);
    try {
      const res = await axios.get('/api/farms', { withCredentials: true });
      const list = res.data.farms || res.data || [];
      setFarms(list);

      if (list.length > 0 && !selectedFarmId) {
        setSelectedFarmId(list[0].id);
        sessionStorage.setItem('selectedFarmId', list[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch farms:', err);
      setError('Failed to load farm list');
    } finally {
      setLoadingFarms(false);
    }
  }, [user, selectedFarmId]);

  useEffect(() => {
    fetchFarms();
  }, [fetchFarms]);

  // 2. Fetch Farm Summary when selected farm changes
  const fetchSummary = useCallback(async (farmId) => {
    if (!farmId) return;
    setLoadingSummary(true);
    setError(null);
    try {
      const res = await axios.get(`/api/farms/${farmId}/summary`, { withCredentials: true });
      setFarmSummary(res.data);

      const plotsList = res.data?.plots || [];
      if (plotsList.length > 0) {
        setSelectedPlotId((prev) => {
          const exists = plotsList.some((p) => (p.site_id || p.id || p.plotId || p.external_id) === prev);
          const firstPlotIdentifier = plotsList[0].external_id || plotsList[0].site_id || plotsList[0].id || plotsList[0].plotId;
          return exists ? prev : firstPlotIdentifier;
        });
      } else {
        setSelectedPlotId(null);
      }
    } catch (err) {
      console.error('Failed to load farm summary:', err);
      setError('Failed to load farm summary');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // 3. Fetch Farm Devices when selected farm changes
  const fetchDevices = useCallback(async (farmId) => {
    if (!farmId) {
      setDevices([]);
      return;
    }
    setLoadingDevices(true);
    try {
      const res = await axios.get(`/api/farms/${farmId}/devices`, { withCredentials: true });
      const deviceList = res.data.devices || res.data || [];
      setDevices(deviceList);
    } catch (err) {
      console.error('Failed to fetch farm devices:', err);
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFarmId) {
      setPlotConditionsCache({});
      fetchSummary(selectedFarmId);
      fetchDevices(selectedFarmId);
    }
  }, [selectedFarmId, fetchSummary, fetchDevices]);

  // 4. Dynamically fetch Plot Conditions when selectedPlotId changes
  useEffect(() => {
    if (!selectedFarmId || !selectedPlotId) return;

    if (plotConditionsCache[selectedPlotId]) {
      return;
    }

    const fetchPlotConditions = async () => {
      setLoadingConditions(true);
      try {
        const res = await axios.get(
          `/api/farms/${selectedFarmId}/plots/${selectedPlotId}/conditions`,
          { withCredentials: true }
        );

        const conditionsData = res.data.conditions || res.data;

        setPlotConditionsCache((prev) => ({
          ...prev,
          [selectedPlotId]: conditionsData
        }));
      } catch (err) {
        console.error(`Failed to fetch conditions for plot ${selectedPlotId}:`, err);
      } finally {
        setLoadingConditions(false);
      }
    };

    fetchPlotConditions();
  }, [selectedFarmId, selectedPlotId, plotConditionsCache]);

  const selectFarm = (farmId) => {
    setSelectedFarmId(farmId);
    sessionStorage.setItem('selectedFarmId', farmId);
  };


  // Map devices down to each plot using site_id, plot_id, external_id, or id
  const plots = useMemo(() => {
    const rawPlots = farmSummary?.plots || [];
    if (!rawPlots.length) return [];

    return rawPlots.map((plot) => {
      const plotKeys = [
        plot.site_id,
        plot.id,
        plot.plotId,
        plot.external_id
      ].filter(Boolean);

      const assignedDevices = devices.filter((d) => {
        const devKey = d.site_id || d.plot_id || d.plotId || d.external_id;
        return plotKeys.includes(devKey);
      });

      // Deduplicate devices by ID
      const uniqueDevices = Array.from(
        new Map(assignedDevices.map((d) => [d.device_id || d.id, d])).values()
      );

      const resolvedDevices = uniqueDevices.length > 0 ? uniqueDevices : (plot.devices || []);
      const primaryDevice = resolvedDevices[0] || plot.device || null;

      return {
        ...plot,
        devices: resolvedDevices,
        device: primaryDevice // Backwards compatibility for code looking for .device
      };
    });
  }, [farmSummary, devices]);

  // Derived farm summary with augmented plots
  const augmentedFarmSummary = useMemo(() => {
    if (!farmSummary) return null;
    return {
      ...farmSummary,
      plots
    };
  }, [farmSummary, plots]);

  // Active plot metadata
  const selectedPlotSummary = useMemo(() => {
    if (!plots.length || !selectedPlotId) return null;
    return plots.find(
      (plot) => (plot.external_id || plot.site_id || plot.plotId || plot.id) === selectedPlotId
    ) || null;
  }, [plots, selectedPlotId]);

  // Active plot conditions
  const selectedPlotConditions = selectedPlotId
    ? plotConditionsCache[selectedPlotId] || null
    : null;

  // Filter devices belonging to the selected plot
  const selectedPlotDevices = useMemo(() => {
    if (!devices.length || !selectedPlotId) return [];
    return devices.filter((d) => {
      const devPlotId = d.site_id || d.plot_id || d.plotId || d.external_id;
      return devPlotId === selectedPlotId;
    });
  }, [devices, selectedPlotId]);

  // Unified plot object combining summary metadata, conditions, and attached devices
  const selectedPlot = useMemo(() => {
    if (!selectedPlotSummary) return null;
    const plotDevices = selectedPlotSummary.devices?.length > 0 ? selectedPlotSummary.devices : selectedPlotDevices;
    return {
      ...selectedPlotSummary,
      conditions: selectedPlotConditions || selectedPlotSummary.conditions || null,
      state_data: selectedPlotConditions || selectedPlotSummary.state_data || null,
      devices: plotDevices,
      device: plotDevices[0] || selectedPlotSummary.device || null
    };
  }, [selectedPlotSummary, selectedPlotConditions, selectedPlotDevices]);

  return (
    <FarmContext.Provider
      value={{
        farms,
        selectedFarmId,
        farmId: selectedFarmId, // Aliased for compatibility
        selectFarm,
        farmSummary: augmentedFarmSummary,
        plots,                    // Direct array of augmented plot objects
        selectedPlotId,
        setSelectedPlotId,
        selectedPlotSummary,
        selectedPlotConditions,
        devices,                  // All devices for the farm
        selectedPlotDevices,      // Devices filtered for the selected plot
        selectedPlot,             // Unified plot object (includes .devices and .device)
        loading: loadingFarms || loadingSummary || loadingDevices,
        loadingFarms,
        loadingSummary,
        loadingConditions,
        loadingDevices,
        error,
        refetchFarms: fetchFarms,
        refreshSummary: () => fetchSummary(selectedFarmId),
        refreshDevices: () => fetchDevices(selectedFarmId)
      }}
    >
      {children}
    </FarmContext.Provider>
  );
};

export const useFarm = () => {
  const context = useContext(FarmContext);
  if (!context) {
    throw new Error('useFarm must be used within a FarmProvider');
  }
  return context;
};
